import { useContext, useMemo } from 'react';
import { countryToISO, TransactionsLists } from 'vopay';
import { TransactionsHistoryHostProps } from 'shared/transactionHistory';
import AccountContext from '@/components/Entities/Accounts/AccountContext';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { ACCOUNTS_PATH } from '@/components/Entities/paths';

/**
 * The one place partner-dashboard maps its account model onto what the shared Transaction History
 * takes as props (ID-13369). Mounted by the account page's Transactions tab, where the list shows one
 * account's transactions through that account's own bearer. Mirrors client-dashboard's
 * useTransactionsHostProps. Only the actions are role-gated — clearing a flag here, Refund on the
 * transaction page — since viewing is never role-gated on the partner side.
 */
export default function useAccountTransactionsHostProps(accountId: string): TransactionsHistoryHostProps {
  const { details, balances } = useContext(AccountContext);

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const account = details[accountId];
  const accountName = account?.accountName ?? '';
  // The listing sends the country as its display name; the shared filter vocabulary keys on ISO.
  const country = countryToISO(account?.address.country ?? '');
  // The list fetch already knows whether the account may hold sub-accounts; the tab bar reads it the
  // same way, so the two never disagree about whether sub-accounts exist.
  const listedAccount = balances.find((item) => item.AccountID === accountId);

  const subAccountPermissions =
    Boolean(listedAccount?.SubAccountsEnabled) || (listedAccount?.SubAccounts ?? []).length > 0;

  return useMemo(
    () => ({
      // The partner portal has no timezone setting to read, so the list keeps its own default (UTC)
      // rather than being handed an invented one; the filter bar still offers the viewer a choice.
      viewer: { timezone: '' },
      account: { country, subAccountPermissions },
      // Viewing is never role-gated on the partner side: every role sees the account's sub-account
      // transactions. Clearing a flag is an action, so it follows the same rule Refund does.
      userPermissions: { canViewSubaccounts: true, canRemoveFlags: !isRoleViewOnly },
      hostPaths: {
        transactionDetail: (transaction: TransactionsLists) => {
          // The route's rail segment is the type with its spaces removed; the page reads the type back
          // off the query with the account name it labels the receiver with, and the transaction's date,
          // which the page needs to fetch the row for rails it has no dedicated view of.
          const search = new URLSearchParams({
            type: transaction.TransactionType,
            accountName,
            date: transaction.TransactionDateTime,
          }).toString();

          return `${ACCOUNTS_PATH}/${accountId}/${transaction.TransactionType.replaceAll(' ', '')}/transaction/${transaction.TransactionID}?${search}`;
        },
      },
    }),
    [accountId, accountName, country, subAccountPermissions, isRoleViewOnly],
  );
}
